import {
  ROLE_LABELS,
  type CatequistaListDto,
  type CreateCatequistaInput,
  type UpdateCatequistaInput,
} from '@sheepcount/shared';
import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { PageLoading } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/toast';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

interface ProfessorDraft {
  nome: string;
  email: string;
  senha: string;
  admin: boolean;
}

const EMPTY_DRAFT: ProfessorDraft = { nome: '', email: '', senha: '', admin: false };

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return 'Já existe um professor com este e-mail';
    return err.message;
  }
  return err instanceof Error ? err.message : fallback;
}

export function ProfessoresPage() {
  const toast = useToast();
  const { catequista } = useAuth();
  const [professores, setProfessores] = useState<CatequistaListDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<CatequistaListDto | null>(null);
  const [draft, setDraft] = useState<ProfessorDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setProfessores(await api<CatequistaListDto[]>('/catequistas'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar professores');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openNew() {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setSheetOpen(true);
  }

  function openEdit(professor: CatequistaListDto) {
    setEditing(professor);
    setDraft({
      nome: professor.nome,
      email: professor.email,
      senha: '',
      admin: professor.role === 'ADMIN',
    });
    setSheetOpen(true);
  }

  async function handleSave() {
    if (!draft.nome.trim()) {
      toast('Informe o nome do professor');
      return;
    }
    if (!draft.email.trim()) {
      toast('Informe o e-mail do professor');
      return;
    }
    const senha = draft.senha;
    if (!editing && senha.length < 8) {
      toast('A senha deve ter ao menos 8 caracteres');
      return;
    }
    if (editing && senha.length > 0 && senha.length < 8) {
      toast('A senha deve ter ao menos 8 caracteres');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload: UpdateCatequistaInput = {
          nome: draft.nome.trim(),
          email: draft.email.trim().toLowerCase(),
          role: draft.admin ? 'ADMIN' : 'CATEQUISTA',
          ...(senha.length > 0 ? { senha } : {}),
        };
        await api(`/catequistas/${editing.id}`, { method: 'PUT', body: payload });
      } else {
        const payload: CreateCatequistaInput = {
          nome: draft.nome.trim(),
          email: draft.email.trim().toLowerCase(),
          senha,
          role: draft.admin ? 'ADMIN' : 'CATEQUISTA',
        };
        await api('/catequistas', { method: 'POST', body: payload });
      }
      setSheetOpen(false);
      toast(editing ? 'Professor atualizado' : 'Professor criado');
      void load();
    } catch (err) {
      toast(errorMessage(err, 'Erro ao salvar professor'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate() {
    if (!editing) return;
    if (!window.confirm('Desativar este professor? Ele não poderá mais fazer login.')) return;
    setSaving(true);
    try {
      await api(`/catequistas/${editing.id}`, { method: 'DELETE' });
      setSheetOpen(false);
      toast('Professor desativado');
      void load();
    } catch (err) {
      toast(errorMessage(err, 'Erro ao desativar professor'));
    } finally {
      setSaving(false);
    }
  }

  async function handleReactivate() {
    if (!editing) return;
    setSaving(true);
    try {
      const payload: UpdateCatequistaInput = { ativo: true };
      await api(`/catequistas/${editing.id}`, { method: 'PUT', body: payload });
      setSheetOpen(false);
      toast('Professor reativado');
      void load();
    } catch (err) {
      toast(errorMessage(err, 'Erro ao reativar professor'));
    } finally {
      setSaving(false);
    }
  }

  const isSelf = editing != null && editing.id === catequista?.id;

  return (
    <div>
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[22px] font-bold tracking-[-0.01em]">Professores</h1>
        <Button size="sm" className="px-4 py-[9px]" onClick={openNew}>
          + Novo professor
        </Button>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3">
          <div className="text-[13px] text-danger">{error}</div>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Tentar novamente
          </Button>
        </div>
      ) : professores === null ? (
        <PageLoading />
      ) : professores.length === 0 ? (
        <div className="text-[13px] text-muted">Nenhum professor cadastrado ainda.</div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-3.5">
          {professores.map((professor) => (
            <Card
              key={professor.id}
              role="button"
              tabIndex={0}
              onClick={() => openEdit(professor)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openEdit(professor);
              }}
              className={cn(
                'flex cursor-pointer flex-col gap-2 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
                !professor.ativo && 'opacity-60',
              )}
            >
              <div className="break-words text-[15px] font-bold">{professor.nome}</div>
              <div className="break-words text-[12.5px] text-muted">{professor.email}</div>
              <div className="mt-0.5 flex flex-wrap gap-1.5">
                <Badge variant={professor.role === 'ADMIN' ? 'primary' : 'default'}>
                  {ROLE_LABELS[professor.role]}
                </Badge>
                {!professor.ativo && <Badge variant="danger">Inativo</Badge>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetTitle>{editing ? 'Editar professor' : 'Novo professor'}</SheetTitle>
          <div className="flex flex-col gap-3.5">
            <div>
              <Label htmlFor="prof-nome">Nome</Label>
              <Input
                id="prof-nome"
                className="mt-1.5"
                placeholder="Nome do professor"
                value={draft.nome}
                onChange={(e) => setDraft((d) => ({ ...d, nome: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="prof-email">E-mail</Label>
              <Input
                id="prof-email"
                className="mt-1.5"
                type="email"
                placeholder="professor@paroquia.com"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="prof-senha">Senha</Label>
              <Input
                id="prof-senha"
                className="mt-1.5"
                type="password"
                autoComplete="new-password"
                placeholder={editing ? 'Deixe em branco para manter a senha atual' : ''}
                value={draft.senha}
                onChange={(e) => setDraft((d) => ({ ...d, senha: e.target.value }))}
              />
              {!editing && <div className="mt-1.5 text-xs text-muted">Mínimo 8 caracteres</div>}
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 text-[13px] font-medium text-fg-soft">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={draft.admin}
                onChange={(e) => setDraft((d) => ({ ...d, admin: e.target.checked }))}
              />
              Perfil administrador
            </label>
          </div>
          <div className="mt-[22px] flex gap-2.5">
            {editing && editing.ativo && !isSelf && (
              <Button
                variant="destructive-outline"
                disabled={saving}
                onClick={() => void handleDeactivate()}
              >
                Desativar
              </Button>
            )}
            {editing && !editing.ativo && (
              <Button
                variant="secondary"
                disabled={saving}
                onClick={() => void handleReactivate()}
              >
                Reativar
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="secondary" disabled={saving} onClick={() => setSheetOpen(false)}>
              Cancelar
            </Button>
            <Button className="font-bold" disabled={saving} onClick={() => void handleSave()}>
              Salvar
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
